import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  LogOut,
  Plus,
  Edit,
  Trash2,
  X,
  Check,
  Users,
  Activity,
  Heart,
  UserPlus,
  Inbox,
  Star,
  Send,
  Mail,
  Loader2,
  Download,
  ShieldCheck,
  Crown,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import PatientsTab from "../components/admin/PatientsTab";
import CaregiversTab from "../components/admin/CaregiversTab";
import StudyInterestsTab from "../components/admin/StudyInterestsTab";
import ReviewsTab from "../components/admin/ReviewsTab";
import SendReviewRequestTab from "../components/admin/SendReviewRequestTab";
import TeamTab from "../components/admin/TeamTab";
import SubscribersTab from "../components/admin/SubscribersTab";
import { AttentionStrip } from "../components/admin/AttentionStrip";
import { StatTile } from "../components/admin/StatTile";
import { InviteAdminModal } from "../components/admin/InviteAdminModal";
import { METHODOLOGY_OPTIONS, RESEARCH_TYPES, DIAGNOSIS_CONFIRMATION_OPTIONS } from "../constants/methodologies";
import { COUNTRIES, getStudyCountries, stripCurrencyPrefix } from "../lib/countries";
import { toIsoDateForInput } from "../lib/dates";
import Flag from "../components/Flag";
import MultiCombobox from "../components/forms/MultiCombobox";
import CompensationAmountInput from "../components/admin/CompensationAmountInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { adminAuthHeader, clearAdminToken, getAdminToken } from "../lib/adminAuth";

const getAuthHeaders = () => adminAuthHeader();

export default function AdminDashboardPage() {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStudy, setEditingStudy] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [teamRefreshKey, setTeamRefreshKey] = useState(0);
  const navigate = useNavigate();

  const isOwner = currentAdmin?.role === "owner";

  useEffect(() => {
    const token = getAdminToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, studiesRes, meRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/studies`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/auth/me`, { headers: getAuthHeaders() }),
      ]);
      setStats(statsRes.data);
      setStudies(studiesRes.data);
      setCurrentAdmin(meRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        clearAdminToken();
        navigate("/admin/login");
      }
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // Best-effort server-side audit log entry , don't block the redirect on
    // it.
    axios
      .post(`${API}/admin/auth/logout`, {}, { headers: getAuthHeaders() })
      .catch(() => {});
    clearAdminToken();
    navigate("/admin/login");
  };

  const handleSeedStudies = async () => {
    try {
      await axios.post(`${API}/admin/seed`, {}, { headers: getAuthHeaders() });
      toast.success("Studies seeded successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to seed studies");
    }
  };

  const handleDeleteStudy = async (id) => {
    if (!window.confirm("Are you sure you want to delete this study?")) return;
    
    try {
      await axios.delete(`${API}/admin/studies/${id}`, { headers: getAuthHeaders() });
      toast.success("Study deleted successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete study");
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;
    
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { headers: getAuthHeaders() });
      toast.success("Review deleted successfully!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const openEditModal = (study = null) => {
    setEditingStudy(study);
    setShowModal(true);
  };

  const handleExportPatients = async () => {
    try {
      const resp = await fetch(`${API}/admin/patients/export`, {
        headers: adminAuthHeader(),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `meditalk-patients-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Patient export downloaded.");
    } catch (err) {
      toast.error("Couldn't export. Try again.");
    }
  };

  const openReviewModal = (review = null) => {
    setEditingReview(review);
    setShowReviewModal(true);
  };

  if (loading) {
    return (
      <div className="admin-shell min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[var(--teal)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--olive)]">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell min-h-screen flex">
      {/* Sidebar */}
      <aside className="admin-sidebar w-64 min-h-screen fixed left-0 top-0 p-6 hidden md:block">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[var(--lime)] flex items-center justify-center">
            <span className="text-[var(--charcoal)] font-bold font-['Outfit']">M</span>
          </div>
          <div>
            <span className="brand-wordmark text-white text-2xl block leading-none">MediTalk</span>
            <span className="text-xs text-[var(--gray-light)] uppercase tracking-wider">Admin</span>
          </div>
        </div>

        <nav className="space-y-2">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`admin-nav-item w-full flex items-center gap-3 ${
              activeTab === "dashboard" ? "active" : "text-[var(--charcoal)]"
            }`}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard size={20} />
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab("studies")}
            className={`admin-nav-item w-full flex items-center gap-3 ${
              activeTab === "studies" ? "active" : "text-[var(--charcoal)]"
            }`}
            data-testid="nav-studies"
          >
            <ClipboardList size={20} />
            Studies
          </button>
          <button
            onClick={() => setActiveTab("patients")}
            className={`admin-nav-item w-full flex items-center gap-3 ${
              activeTab === "patients" ? "active" : "text-[var(--charcoal)]"
            }`}
            data-testid="nav-patients"
          >
            <UserPlus size={20} />
            Patients
          </button>
          <button
            onClick={() => setActiveTab("caregivers")}
            className={`admin-nav-item w-full flex items-center gap-3 ${
              activeTab === "caregivers" ? "active" : "text-[var(--charcoal)]"
            }`}
            data-testid="nav-caregivers"
          >
            <Users size={20} />
            Caregivers
          </button>
          <button
            onClick={() => setActiveTab("interests")}
            className={`admin-nav-item w-full flex items-center gap-3 ${
              activeTab === "interests" ? "active" : "text-[var(--charcoal)]"
            }`}
            data-testid="nav-interests"
          >
            <Inbox size={20} />
            Interests
          </button>
          <button
            onClick={() => setActiveTab("reviews")}
            className={`admin-nav-item w-full flex items-center gap-3 ${
              activeTab === "reviews" ? "active" : "text-[var(--charcoal)]"
            }`}
            data-testid="nav-reviews"
          >
            <Star size={20} />
            Reviews
          </button>
          <button
            onClick={() => setActiveTab("send-review-request")}
            className={`admin-nav-item w-full flex items-center gap-3 ${
              activeTab === "send-review-request" ? "active" : "text-[var(--charcoal)]"
            }`}
            data-testid="nav-send-review-request"
          >
            <Send size={20} />
            Send review request
          </button>
          {isOwner && (
            <button
              onClick={() => setActiveTab("team")}
              className={`admin-nav-item w-full flex items-center gap-3 ${
                activeTab === "team" ? "active" : "text-[var(--charcoal)]"
              }`}
              data-testid="nav-team"
            >
              <Crown size={20} />
              Team
            </button>
          )}
          <button
            onClick={() => setActiveTab("subscribers")}
            className={`admin-nav-item w-full flex items-center gap-3 ${
              activeTab === "subscribers" ? "active" : "text-[var(--charcoal)]"
            }`}
            data-testid="nav-subscribers"
          >
            <Mail size={20} />
            Subscribers
          </button>
        </nav>

        <button
          onClick={handleLogout}
          className="admin-nav-item w-full flex items-center gap-3 mt-8 hover:!text-red-300"
          data-testid="logout-btn"
        >
          <LogOut size={20} />
          Logout
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 p-8">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[var(--lime)] flex items-center justify-center">
              <span className="text-[var(--charcoal)] font-bold text-sm font-['Outfit']">M</span>
            </div>
            <span className="font-bold font-['Outfit'] text-[var(--charcoal)]">Admin</span>
          </div>
          <button onClick={handleLogout} className="text-[var(--destructive-color)]">
            <LogOut size={20} />
          </button>
        </div>

        {/* Mobile Tabs */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab("dashboard")}
            className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium border ${
              activeTab === "dashboard"
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)]"
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab("studies")}
            className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium border ${
              activeTab === "studies"
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)]"
            }`}
          >
            Studies
          </button>
          <button
            onClick={() => setActiveTab("patients")}
            className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium border ${
              activeTab === "patients"
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)]"
            }`}
          >
            Patients
          </button>
          <button
            onClick={() => setActiveTab("caregivers")}
            className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium border ${
              activeTab === "caregivers"
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)]"
            }`}
          >
            Caregivers
          </button>
          <button
            onClick={() => setActiveTab("interests")}
            className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium border ${
              activeTab === "interests"
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)]"
            }`}
          >
            Interests
          </button>
          <button
            onClick={() => setActiveTab("reviews")}
            className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium border ${
              activeTab === "reviews"
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)]"
            }`}
          >
            Reviews
          </button>
          <button
            onClick={() => setActiveTab("send-review-request")}
            className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium border ${
              activeTab === "send-review-request"
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)]"
            }`}
          >
            Send request
          </button>
          <button
            onClick={() => setActiveTab("subscribers")}
            className={`flex-shrink-0 py-2 px-4 rounded-lg text-sm font-medium border ${
              activeTab === "subscribers"
                ? "bg-[var(--charcoal)] text-white border-[var(--charcoal)]"
                : "bg-white text-[var(--charcoal)] border-[var(--light-border)]"
            }`}
          >
            Subscribers
          </button>
        </div>

        {activeTab === "dashboard" && (
          <>
            <AttentionStrip stats={stats} onJumpToTab={setActiveTab} />

            {/* Page header + Quick Actions row */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold tracking-tight text-[var(--dark-bg)]">
                  Dashboard
                </h1>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Snapshot of what's live across MediTalk right now.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => {
                    setActiveTab("studies");
                    openEditModal();
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] text-sm font-bold hover:bg-[var(--lime-dark)] transition-colors shadow-sm"
                  data-testid="add-study-quick"
                >
                  <Plus size={16} strokeWidth={2.5} />
                  Add new study
                </button>
                <button
                  onClick={handleExportPatients}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[var(--charcoal)] border border-[var(--light-border)] text-sm font-semibold hover:bg-[var(--bone-soft)] transition-colors"
                  data-testid="export-patients-quick"
                >
                  <Download size={16} />
                  Export patients CSV
                </button>
                {isOwner && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[var(--charcoal)] border border-[var(--light-border)] text-sm font-semibold hover:bg-[var(--bone-soft)] transition-colors"
                    data-testid="invite-admin-quick"
                  >
                    <ShieldCheck size={16} />
                    Invite admin
                  </button>
                )}
              </div>
            </div>

            {/* 4-tile bento grid , wraps cleanly at all widths */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
              <StatTile
                icon={<Activity size={18} strokeWidth={2} />}
                title="Active studies"
                value={stats?.active_studies || 0}
                subLabel={`Out of ${stats?.total_studies || 0} total · ${stats?.patient_studies || 0} patient · ${stats?.caregiver_studies || 0} caregiver`}
                delta={stats?.active_studies_delta_30d}
                deltaLabel="this month"
                testIdRoot="stat-tile-studies"
                onClick={() => setActiveTab("studies")}
              />
              <StatTile
                icon={<Heart size={18} strokeWidth={2} />}
                title="Active patients"
                value={stats?.active_patients || 0}
                subLabel={`Database total: ${stats?.total_patients || 0}`}
                delta={stats?.patients_delta_30d}
                deltaLabel="this month"
                testIdRoot="stat-tile-patients"
                onClick={() => setActiveTab("patients")}
              />
              <StatTile
                icon={<Users size={18} strokeWidth={2} />}
                title="Active caregivers"
                value={stats?.active_caregivers || 0}
                subLabel={`Database total: ${stats?.total_caregivers || 0}`}
                delta={stats?.caregivers_delta_30d}
                deltaLabel="this month"
                testIdRoot="stat-tile-caregivers"
                onClick={() => setActiveTab("caregivers")}
              />
              <StatTile
                icon={<Inbox size={18} strokeWidth={2} />}
                title="Study interests"
                value={stats?.total_study_interests || 0}
                subLabel={
                  stats?.new_study_interests > 0
                    ? `${stats.new_study_interests} need follow-up · ${stats?.new_interests_7d || 0} new this week`
                    : `${stats?.new_interests_7d || 0} new this week`
                }
                delta={stats?.interests_delta_30d}
                deltaLabel="this month"
                testIdRoot="stat-tile-interests"
                onClick={() => setActiveTab("interests")}
              />
            </div>

            {/* Empty-state nudge: only render when there are no studies yet */}
            {studies.length === 0 && (
              <div
                className="mt-6 rounded-2xl border border-dashed border-[var(--light-border)] bg-white p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                data-testid="empty-studies-card"
              >
                <div>
                  <p className="font-['Outfit'] font-bold text-[var(--charcoal)] text-base">
                    No studies yet
                  </p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">
                    Seed a couple of sample studies to see how the public list looks.
                  </p>
                </div>
                <button
                  onClick={handleSeedStudies}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--charcoal)] text-white text-sm font-semibold hover:bg-[var(--dark-bg)]"
                  data-testid="seed-studies-btn"
                >
                  Seed sample studies
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === "studies" && (
          <>
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-2xl font-['Outfit'] font-bold text-[var(--charcoal)]">
                Manage Studies
              </h1>
              <button
                onClick={() => openEditModal()}
                className="btn-primary flex items-center gap-2"
                data-testid="add-study-btn"
              >
                <Plus size={18} />
                Add Study
              </button>
            </div>

            {/* Studies Table */}
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Condition</th>
                      <th>Country</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studies.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-[var(--olive)]">
                          No studies found. Add your first study or seed sample data.
                        </td>
                      </tr>
                    ) : (
                      studies.map((study) => (
                        <tr key={study.id}>
                          <td className="font-medium text-[var(--charcoal)] max-w-[200px] truncate">
                            {study.title}
                          </td>
                          <td>{study.condition}</td>
                          <td>
                            {(() => {
                              const list = getStudyCountries(study);
                              if (list.length === 0) return study.country || "—";
                              return (
                                <span
                                  title={list.map((c) => c.name).join(", ")}
                                  className="inline-flex items-center gap-1"
                                >
                                  {list.slice(0, 3).map((c) => (
                                    <Flag key={c.name} code={c.code} size="sm" />
                                  ))}
                                  <span className="ml-1">
                                    {list.length === 1
                                      ? list[0].name
                                      : list.length === 2
                                        ? "2 countries"
                                        : `${list.length} countries`}
                                  </span>
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            <span className={`badge ${study.study_type === "patient" ? "badge-terracotta" : "badge-sage"}`}>
                              {study.study_type}
                            </span>
                          </td>
                          <td>
                            {study.is_active ? (
                              <span className="flex items-center gap-1 text-[var(--success)]">
                                <Check size={16} /> Active
                              </span>
                            ) : (
                              <span className="text-[var(--olive)]">Inactive</span>
                            )}
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditModal(study)}
                                className="p-2 hover:bg-[var(--sage)]/10 rounded-lg transition-colors"
                                data-testid={`edit-study-${study.id}`}
                              >
                                <Edit size={16} className="text-[var(--sage)]" />
                              </button>
                              <button
                                onClick={() => handleDeleteStudy(study.id)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                data-testid={`delete-study-${study.id}`}
                              >
                                <Trash2 size={16} className="text-red-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === "patients" && <PatientsTab />}
        {activeTab === "caregivers" && <CaregiversTab />}
        {activeTab === "interests" && <StudyInterestsTab />}
        {activeTab === "reviews" && <ReviewsTab />}
        {activeTab === "send-review-request" && <SendReviewRequestTab />}
        {activeTab === "team" && isOwner && (
          <TeamTab
            key={teamRefreshKey}
            currentEmail={currentAdmin?.email}
            onInvite={() => setShowInviteModal(true)}
          />
        )}
        {activeTab === "subscribers" && (
          <SubscribersTab getAuthHeaders={getAuthHeaders} />
        )}
      </main>

      {/* Study Modal */}
      {showModal && (
        <StudyModal
          study={editingStudy}
          onClose={() => {
            setShowModal(false);
            setEditingStudy(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingStudy(null);
            fetchData();
          }}
        />
      )}

      {/* Invite admin modal */}
      {showInviteModal && (
        <InviteAdminModal
          onClose={() => {
            setShowInviteModal(false);
            // Bump the key so the Team tab refetches if it's currently
            // open , this surfaces the new pending invite immediately.
            setTeamRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}

// Study Modal Component
function StudyModal({ study, onClose, onSave }) {
  // Hydrate initial state. Existing studies may have `countries`
  // already (post-migration) or only the legacy `country` field
  // (pre-migration / freshly imported), so we use the same
  // `getStudyCountries` helper as the public side. The form keeps a
  // canonical list of country names; flags + free-text fall-back
  // happen at render time.
  const initialCountries = study
    ? getStudyCountries(study).map((c) => c.name)
    : [];
  // Strip the leading currency symbol off pre-existing per-country
  // amounts (e.g. legacy "£100" -> "100") so the admin sees the bare
  // number the new spec asks them to type.
  const initialCompByCountry =
    study?.compensation_by_country && typeof study.compensation_by_country === "object"
      ? Object.fromEntries(
          Object.entries(study.compensation_by_country).map(([k, v]) => [
            k,
            stripCurrencyPrefix(k, v),
          ]),
        )
      : {};
  // For single-country studies, the legacy `compensation` may also
  // have a symbol in it. Strip against the (single) country if known,
  // else leave as-is.
  const initialCompensation = (() => {
    if (!study?.compensation) return "";
    if (initialCountries.length === 1) {
      return stripCurrencyPrefix(initialCountries[0], study.compensation);
    }
    return study.compensation;
  })();
  // research_types is the new multi-select. If the study only has the
  // legacy single-string `format`, seed the array from it so the admin
  // doesn't have to re-pick.
  const initialResearchTypes =
    Array.isArray(study?.research_types) && study.research_types.length > 0
      ? [...study.research_types]
      : study?.format
        ? [study.format]
        : [];

  const [formData, setFormData] = useState({
    // Title is auto-derived server-side from condition + study_type;
    // we keep it here so PUTs that haven't changed the condition still
    // round-trip the existing value.
    title: study?.title || "",
    condition: study?.condition || "",
    description: study?.description || "",
    project_number: study?.project_number || "",
    research_types: initialResearchTypes,
    research_type_other: study?.research_type_other || "",
    // `countries` is the structured list of names we send to the server;
    // `country` (legacy free-text) is auto-derived server-side and kept
    // in the model for backwards compat. The admin no longer types
    // country names by hand.
    countries: initialCountries,
    compensation: initialCompensation,
    compensation_by_country: initialCompByCountry,
    end_date: toIsoDateForInput(study?.end_date) || "",
    platform: study?.platform || "Zoom",
    format: study?.format || "",
    time_commitment: study?.time_commitment || "",
    diagnosis_confirmation: study?.diagnosis_confirmation || "",
    screener_url: study?.screener_url || "",
    image_url: study?.image_url || "",
    registration_url: study?.registration_url || "",
    study_type: study?.study_type || "",
    is_active: study?.is_active ?? true,
  });
  const [loading, setLoading] = useState(false);

  const isMultiCountry = formData.countries.length >= 2;
  const isOtherSelected = formData.research_types.some(
    (t) => t.toLowerCase().startsWith("other"),
  );

  const setCompForCountry = (country, value) => {
    setFormData((prev) => ({
      ...prev,
      compensation_by_country: {
        ...prev.compensation_by_country,
        [country]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_number?.trim()) {
      toast.error("Please add a project number.");
      return;
    }
    if (formData.research_types.length === 0) {
      toast.error("Please pick at least one research type.");
      return;
    }
    if (isOtherSelected && !formData.research_type_other.trim()) {
      toast.error("Please specify the 'Other' research type.");
      return;
    }
    if (!formData.diagnosis_confirmation) {
      toast.error("Please pick a confirmation of diagnosis / treatment option.");
      return;
    }
    if (!formData.study_type) {
      toast.error("Please pick the audience (Patient or Caregiver).");
      return;
    }
    if (formData.countries.length === 0) {
      toast.error("Please pick at least one country for this study.");
      return;
    }
    if (isMultiCountry) {
      const missing = formData.countries.filter(
        (c) => !formData.compensation_by_country?.[c]?.trim(),
      );
      if (missing.length > 0) {
        toast.error(
          `Please add a compensation amount for: ${missing.join(", ")}`,
        );
        return;
      }
    } else if (!formData.compensation?.trim()) {
      toast.error("Please add a compensation amount.");
      return;
    }
    setLoading(true);

    try {
      // For single-country studies we always clear the
      // compensation_by_country map (the legacy free-text
      // `compensation` field is enough and we don't want orphan
      // entries to drift). For multi-country studies we send the map
      // and ALSO keep a sensible `compensation` summary so any
      // un-migrated read path still shows something.
      const payload = { ...formData };
      if (isMultiCountry) {
        payload.compensation =
          payload.compensation?.trim() ||
          Object.entries(payload.compensation_by_country)
            .map(([c, v]) => `${v} ${c}`)
            .join(" · ");
      } else {
        payload.compensation_by_country = null;
      }
      if (study) {
        await axios.put(`${API}/admin/studies/${study.id}`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Study updated successfully!");
      } else {
        await axios.post(`${API}/admin/studies`, payload, {
          headers: getAuthHeaders(),
        });
        toast.success("Study created successfully!");
      }
      onSave();
    } catch (error) {
      toast.error("Failed to save study");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="admin-modal-panel bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[var(--sage)]/10 flex items-center justify-between">
          <h2 className="text-xl font-['Outfit'] font-semibold text-[var(--charcoal)]">
            {study ? "Edit Study" : "Add New Study"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--sage)]/10 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Visibility toggle , prominent at top */}
          <button
            type="button"
            onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
            className={`w-full flex items-center justify-between gap-4 p-4 rounded-xl border-2 transition-all ${
              formData.is_active
                ? "border-[var(--lime)] bg-[var(--lime)]/12"
                : "border-[var(--light-border)] bg-[var(--bone-soft)]"
            }`}
            data-testid="study-active-toggle"
          >
            <div className="text-left">
              <div className="font-semibold text-[var(--charcoal)] flex items-center gap-2">
                <span
                  className={`inline-block w-2 h-2 rounded-full ${
                    formData.is_active ? "bg-[var(--success)]" : "bg-gray-400"
                  }`}
                />
                {formData.is_active ? "Live on the website" : "Hidden from the website"}
              </div>
              <div className="text-xs text-[var(--olive)] mt-0.5">
                {formData.is_active
                  ? "Patients and caregivers can see this study and express interest."
                  : "Only admins can see this study. Tap to publish."}
              </div>
            </div>
            <div
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                formData.is_active ? "bg-[var(--success)]" : "bg-gray-300"
              }`}
              role="switch"
              aria-checked={formData.is_active}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  formData.is_active ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </div>
            {/* Hidden checkbox keeps the existing testid working */}
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={() => {}}
              className="sr-only"
              data-testid="study-active"
              tabIndex={-1}
              aria-hidden="true"
            />
          </button>

          <div className="flex flex-col gap-4">
            {/* 1. Condition */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Condition / disease *
              </label>
              <input
                type="text"
                value={formData.condition}
                onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                className="w-full h-10 px-4 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20"
                required
                data-testid="study-condition"
              />
              <p className="text-xs text-[var(--olive)] mt-1">
                Shown on the website. The study title is derived from
                this automatically.
              </p>
            </div>

            {/* 2. Project number , shown as a small reference on
                the public study card so patients can quote it back
                to you when emailing. */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Project number *
              </label>
              <input
                type="text"
                value={formData.project_number}
                onChange={(e) => setFormData({ ...formData, project_number: e.target.value })}
                className="w-full h-10 px-4 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20"
                required
                data-testid="study-project-number"
              />
              <p className="text-xs text-[var(--olive)] mt-1">
                Your project / job reference. Shown as a small
                "Ref" label on the public study card so patients can
                quote it when contacting you.
              </p>
            </div>

            {/* 3. Research type (multi-select + Other, specify) */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Research type *
              </label>
              <p className="text-xs text-[var(--olive)] mb-2">
                Pick every research type this study uses. If none of
                the presets fits, pick "Other, specify" and write it in.
              </p>
              <MultiCombobox
                data-testid="study-research-types-picker"
                placeholder="Search and pick research types…"
                emptyLabel="No research types match"
                options={RESEARCH_TYPES.map((rt) => ({
                  value: rt.label,
                  label: rt.label,
                }))}
                value={formData.research_types}
                onChange={(next) => {
                  const stillHasOther = next.some((t) =>
                    t.toLowerCase().startsWith("other"),
                  );
                  setFormData((prev) => ({
                    ...prev,
                    research_types: next,
                    research_type_other: stillHasOther
                      ? prev.research_type_other
                      : "",
                  }));
                }}
              />
              {isOtherSelected && (
                <input
                  type="text"
                  value={formData.research_type_other}
                  onChange={(e) =>
                    setFormData({ ...formData, research_type_other: e.target.value })
                  }
                  className="w-full mt-2 h-10 px-4 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20"
                  placeholder="Specify the other research type"
                  data-testid="study-research-type-other-text"
                />
              )}
            </div>

            {/* 4. Description , with helper prompt */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Description *
              </label>
              <p className="text-xs text-[var(--olive)] mb-2">
                Tip: write this from the Informed Consent Form if you
                have one, or from the screener if you don't. This is
                what patients read on the study card.
              </p>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
                className="w-full px-4 py-2 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20 resize-y"
                required
                data-testid="study-description"
              />
            </div>

            {/* 5. Time commitment */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Time commitment *
              </label>
              <input
                type="text"
                value={formData.time_commitment}
                onChange={(e) => setFormData({ ...formData, time_commitment: e.target.value })}
                className="w-full h-10 px-4 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20"
                required
                data-testid="study-time-commitment"
              />
            </div>

            {/* 6. Confirmation of Diagnosis / Treatment */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Confirmation of diagnosis / treatment *
              </label>
              <select
                value={formData.diagnosis_confirmation}
                onChange={(e) => setFormData({ ...formData, diagnosis_confirmation: e.target.value })}
                className="w-full h-10 px-4 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20"
                data-testid="study-diagnosis-confirmation"
                required
              >
                <option value="" disabled>Select…</option>
                {DIAGNOSIS_CONFIRMATION_OPTIONS.map((opt) => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 7. Countries (NOT shown on website as standalone fact) */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Countries *
              </label>
              <p className="text-xs text-[var(--olive)] mb-2">
                Pick every country this study accepts participants from.
              </p>
              <MultiCombobox
                data-testid="study-countries-picker"
                placeholder="Search and pick countries…"
                emptyLabel="No countries match"
                options={COUNTRIES.map((c) => ({
                  value: c.name,
                  label: c.name,
                  code: c.code,
                }))}
                value={formData.countries}
                onChange={(next) => {
                  // Drop compensation entries for countries that are
                  // no longer in the selection so we never leak orphans.
                  setFormData((prev) => {
                    const cleaned = {};
                    for (const [k, v] of Object.entries(
                      prev.compensation_by_country || {},
                    )) {
                      if (next.includes(k)) cleaned[k] = v;
                    }
                    return {
                      ...prev,
                      countries: next,
                      compensation_by_country: cleaned,
                    };
                  });
                }}
                renderChip={(opt) => (
                  <span className="inline-flex items-center gap-1.5">
                    <Flag code={opt.code} size="sm" />
                    <span>{opt.label}</span>
                  </span>
                )}
                renderOption={(opt) => (
                  <>
                    <Flag code={opt.code} size="sm" />
                    <span>{opt.label}</span>
                  </>
                )}
              />
            </div>

            {/* 8. Closing date */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Closing date *
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full h-10 px-4 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20 text-[var(--charcoal)]"
                required
                data-testid="study-end-date"
              />
            </div>

            {/* 9. Compensation , one row per selected country.
                Single- and multi-country share the same row shape:
                  flag + country name + [symbol] + numeric input.
                The admin only types the number; the website prepends
                the currency symbol automatically based on the
                country (so "100" against UK renders "£100"). */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Compensation *
              </label>
              <div className="flex items-start gap-2 mb-2 rounded-md border border-[var(--lime)]/40 bg-[var(--lime)]/10 px-3 py-2">
                <span
                  aria-hidden="true"
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] text-[11px] font-bold flex-shrink-0 mt-0.5"
                >
                  !
                </span>
                <p className="text-xs text-[var(--charcoal)] leading-snug">
                  <strong>Important:</strong> type just the number , no
                  currency sign, no abbreviation. The public study card
                  will show the correct symbol automatically based on the
                  country.
                </p>
              </div>
              {formData.countries.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[var(--sage)]/30 bg-[var(--bone-soft)] px-4 py-6 text-center text-sm text-[var(--text-muted)]">
                  Pick at least one country above to set the
                  compensation amount.
                </div>
              ) : (
                <div className="space-y-2" data-testid="study-comp-by-country">
                  {formData.countries.map((country) => {
                    const code = COUNTRIES.find((x) => x.name === country)?.code;
                    // Single-country flow uses the legacy
                    // `compensation` field; multi-country uses
                    // `compensation_by_country[country]`.
                    const isSingle = formData.countries.length === 1;
                    const value = isSingle
                      ? formData.compensation
                      : formData.compensation_by_country?.[country] || "";
                    const onChange = (val) => {
                      if (isSingle) {
                        setFormData((prev) => ({ ...prev, compensation: val }));
                      } else {
                        setCompForCountry(country, val);
                      }
                    };
                    return (
                      <CompensationAmountInput
                        key={country}
                        country={country}
                        value={value}
                        onChange={onChange}
                        required
                        testId={`study-comp-${code || country}`}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Audience , controls whether the study appears on
                /patient/studies or /caregiver/studies. Surfaced
                inline rather than tucked into an Advanced panel so
                admins don't miss it. */}
            <div>
              <label className="block text-sm font-bold text-[var(--charcoal)] mb-1">
                Audience *
              </label>
              <select
                value={formData.study_type}
                onChange={(e) => setFormData({ ...formData, study_type: e.target.value })}
                className="w-full h-10 px-4 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20"
                data-testid="study-type"
                required
              >
                <option value="" disabled>Select…</option>
                <option value="patient">Patient</option>
                <option value="caregiver">Caregiver</option>
              </select>
              <p className="text-xs text-[var(--olive)] mt-1">
                Determines whether this appears on /patient/studies
                or /caregiver/studies.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--charcoal)] mb-1">
                Screener URL (optional)
              </label>
              <input
                type="url"
                value={formData.screener_url}
                onChange={(e) => setFormData({ ...formData, screener_url: e.target.value })}
                className="w-full h-10 px-4 rounded-lg border border-[var(--sage)]/20 focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--sage)]/20"
                placeholder="https://… (leave blank if no screener)"
                data-testid="study-screener-url"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-[var(--sage)]/10">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary disabled:opacity-50"
              data-testid="save-study-btn"
            >
              {loading ? "Saving..." : study ? "Update Study" : "Create Study"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

